import type { Security } from '@talos/kyoko';
import { useCallback, useMemo } from 'react';
import { useFeatureFlag } from '../../../../hooks';
import { isSecuritySelectableInRFQForm } from '../../../OMS/NewRFQ/utils';
import type { PriceGridLadderColumnConfig } from '../../columns/priceGridMarketDataQtyColumns';
import type { PriceGridPrimingContext } from '../../hooks/actions/common/types';
import { usePriceGridMarketPriceAction } from '../../hooks/actions/primeOrder/usePriceGridMarketPriceAction';
import { useRFQAction } from '../../hooks/actions/rfq/useRFQAction';
import type { PriceGridMarketDataColumnHandlers } from '../../hooks/dynamics/types';
import { getGenericPriceGridColumns } from './columns';

export function useGenericPriceGridColumns({
  isBBOEnabled,
  ladderConfig,
  primingContext,
  showRFQ,
}: {
  isBBOEnabled: boolean;
  ladderConfig: PriceGridLadderColumnConfig;
  primingContext?: PriceGridPrimingContext;
  showRFQ: boolean;
}) {
  const { enableRFQFormFundSecurities, enableRFQFormOptions, enablePredictionMarkets } = useFeatureFlag();
  const onRFQClick = useRFQAction(primingContext);
  const { clickToTradePreviewOrder, maxOrderSizeDialogs, onCancelClickToTradePreview, onPrimeOrderClick } =
    usePriceGridMarketPriceAction(primingContext, ladderConfig.priceIncrement);
  const columns = useMemo(
    () => getGenericPriceGridColumns({ isBBOEnabled, ladderConfig, showRFQ }),
    [isBBOEnabled, ladderConfig, showRFQ]
  );
  const isRFQSecuritySelectable = useCallback(
    (security: Security | undefined) =>
      isSecuritySelectableInRFQForm({
        security,
        enableRFQFormFundSecurities,
        enableRFQFormOptions,
        enablePredictionMarkets,
      }),
    [enableRFQFormFundSecurities, enableRFQFormOptions, enablePredictionMarkets]
  );
  const priceGridMarketDataHandlers = useMemo(
    (): PriceGridMarketDataColumnHandlers => ({
      onRFQClick,
      onPrimeOrderClick,
      onCancelClickToTradePreview,
      isRFQSecuritySelectable,
    }),
    [isRFQSecuritySelectable, onCancelClickToTradePreview, onPrimeOrderClick, onRFQClick]
  );

  return {
    columns,
    clickToTradePreviewOrder,
    maxOrderSizeDialogs,
    priceGridMarketDataHandlers,
    ladderConfig,
  };
}
